import type { AdminMembershipNoticeSummary } from "@/types/admin-notice";

/**
 * Normalise `GET /admin/membership-notices/summary`.
 *
 * The real gateway nests the tallies under `counts`:
 *   { "counts": { "pending": 0, "sent": 1040, "cancelled": 0, "failed": 0 } }
 * The previous mapping read top-level `sent`/`failed`/`pending`, so the
 * NoticeHealthCard (and dunning-ops summary) rendered 0/0/0 even with 1040 sent.
 *
 * Reads `counts.*` first, falls back to a legacy flat shape, and derives `total`
 * from the tallies when the backend sends no explicit total.
 */
function asRecord(v: unknown): Record<string, unknown> {
  return typeof v === "object" && v !== null ? (v as Record<string, unknown>) : {};
}

function firstNumber(
  raw: Record<string, unknown>,
  ...keys: string[]
): number | null {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return null;
}

export function normalizeNoticeSummary(raw: unknown): AdminMembershipNoticeSummary {
  const r = asRecord(raw);
  const counts = asRecord(r.counts);

  const pending = firstNumber(counts, "pending") ?? firstNumber(r, "pending") ?? 0;
  const sent = firstNumber(counts, "sent") ?? firstNumber(r, "sent") ?? 0;
  const failed = firstNumber(counts, "failed") ?? firstNumber(r, "failed") ?? 0;
  const cancelled =
    firstNumber(counts, "cancelled") ?? firstNumber(r, "cancelled") ?? 0;
  const duePending =
    firstNumber(counts, "duePending", "due_pending") ??
    firstNumber(r, "duePending", "due_pending") ??
    0;

  const explicitTotal = firstNumber(r, "total");
  const total = explicitTotal ?? pending + sent + failed + cancelled;

  const byTypeRaw = asRecord(r.byType ?? r.by_type);
  const byType: Record<string, number> = {};
  for (const [k, v] of Object.entries(byTypeRaw)) {
    if (typeof v === "number" && Number.isFinite(v)) byType[k] = v;
  }

  return {
    total,
    pending,
    sent,
    failed,
    duePending,
    byType: Object.keys(byType).length > 0 ? byType : undefined,
  };
}
